import { useEffect, useRef, useState, useCallback } from "react";
import { searchUsers, AdminUserLite } from "../../services/adminUsers";

export function useUserSearch() {
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminUserLite[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncer = useRef<number | null>(null);

  const canSearch = term.trim().length >= 2;

  const reset = () => {
    setItems([]);
    setPage(1);
    setTotalPages(1);
  };

  const runSearch = useCallback(async (q: string, p = 1) => {
    if (q.trim().length < 2) {
      setItems([]);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await searchUsers(q, p, 10);
      setItems((prev) => (p === 1 ? res.items : [...prev, ...res.items]));
      setTotalPages(res.pages || 1);
    } catch (e: any) {
      setError(e?.message ?? "Search failed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce on term change
  useEffect(() => {
    reset();
    if (debouncer.current) window.clearTimeout(debouncer.current);
    debouncer.current = window.setTimeout(() => {
      runSearch(term, 1);
    }, 300);
    return () => {
      if (debouncer.current) window.clearTimeout(debouncer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const loadMore = async () => {
    if (loading) return;
    if (page >= totalPages) return;
    const next = page + 1;
    setPage(next);
    await runSearch(term, next);
  };

  return {
    term,
    setTerm,
    items,
    loading,
    error,
    canSearch,
    page,
    totalPages,
    loadMore,
    reset,
  };
}
